import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

export const DEFAULT_CONTENT = {
  hero_kicker: "The Kinetic Pulse of Athletic Culture",
  hero_line1: "RAW MOTION.",
  hero_line2: "SHARP FRAMES.",
  hero_line3: "REAL STORIES.",
  hero_sub:
    "Ekal Sports Media is a photography-first news outlet. We live on the glass, in the tunnel, and at the finish line — filing match reports written in shutter speeds.",
  hero_image:
    "https://images.unsplash.com/photo-1635209985571-70128050869e?crop=entropy&cs=srgb&fm=jpg&q=85",
  hero_badges: ["ISO 3200", "1/2000 SEC", "400MM F/2.8", "COURTSIDE — GAME 7"],
  marquee_items: [
    "BREAKING: GAME 7 GOES TO OVERTIME — FULL GALLERY LIVE",
    "ISO 3200 · 1/2000S · F/2.8 · 400MM",
    "DERBY NIGHT: 24 FRAMES FROM FIELD LEVEL",
    "40.7128° N / 74.0060° W — FIELD LEVEL POSITION",
    "PHOTO FINISH DECIDED BY 0.01 SECONDS",
    "NOW BOOKING: MATCHDAY COVERAGE — SUMMER WINDOW",
    "SHOT ON GLASS · EDITED AT 3AM · FILED BY DAWN",
  ],
  about_intro:
    "Four rules govern every assignment we take — from a sold-out derby to a private tournament at dawn. They are not guidelines. They are the house style.",
  about_image:
    "https://images.unsplash.com/photo-1761258772329-751d7fb43cff?crop=entropy&cs=srgb&fm=jpg&q=85",
  chapters: [
    {
      num: "01",
      title: "We shoot the in-between",
      text: "Anyone can photograph the goal. We live for the tremor before it — the clenched jaw, the laces pulled tight, the breath held by thousands at once. The in-between is where sport tells the truth.",
    },
    {
      num: "02",
      title: "Speed is our language",
      text: "A shutter at 1/4000th of a second doesn't just freeze motion, it dissects it. Our editors read contact sheets like match reports, because every frame is a sentence and the sequence is the story.",
    },
    {
      num: "03",
      title: "Craft over chaos",
      text: "Sidelines are chaos. We bring discipline: mapped positions, pre-visualised frames, redundant bodies, and a post-production desk that treats colour like typography. Precision is the aesthetic.",
    },
    {
      num: "04",
      title: "Access is everything",
      text: "The tunnel, the warm-up, the away end, the mixed zone at midnight. We earn the positions nobody hands out, and we protect them by delivering work nobody else can file.",
    },
  ],
  services_intro:
    "Four ways to put Ekal glass on your game, brand, or tournament. Every package includes an accredited crew and full licensing terms.",
  packages: [
    {
      name: "Matchday Coverage",
      price: "FROM $450",
      tag: "MOST BOOKED",
      features: ["Full match gallery — 100+ frames", "24-hour digital turnaround", "Social-ready selects at halftime", "Two accredited photographers"],
    },
    {
      name: "Athletic Brand Campaign",
      price: "FROM $1,800",
      tag: "",
      features: ["Concept + art direction", "Studio or location production", "Retouched hero set (15 frames)", "Global usage licensing"],
    },
    {
      name: "Editorial Feature",
      price: "FROM $900",
      tag: "",
      features: ["Long-form photo story", "Interview + access support", "Magazine-grade colour edits", "Print-ready master files"],
    },
    {
      name: "Private Tournament Press",
      price: "FROM $1,200",
      tag: "",
      features: ["Multi-day event coverage", "Same-day live delivery", "Medal + ceremony sets", "Wire distribution to press"],
    },
  ],
  contact_intro:
    "Tips, credentials, licensing, or a crew for your next fixture — the desk reads everything, usually from the media room.",
  contact_rows: [
    { label: "Press Desk", value: "press@ekalsports.media" },
    { label: "Bookings", value: "+1 (212) 555-0173" },
    { label: "Studio", value: "Unit 4B — 88 Grand St, NYC" },
    { label: "Wire Hours", value: "Matchdays · 06:00 — 02:00 ET" },
  ],
  footer_tagline: "The kinetic pulse of athletic culture",
  nav_links: [
    { label: "Sports Coverage", target: "#coverage", testid: "nav-sports-coverage", visible: true },
    { label: "Photo Library", target: "#library", testid: "nav-photo-library", visible: true },
    { label: "Contact", target: "#contact", testid: "nav-contact", visible: true },
  ],
  color_accent: "#FF3B30",
  color_amber: "#FFB800",
  color_bg: "#0A0C10",
};

const SiteContentContext = createContext(DEFAULT_CONTENT);

export const useSiteContent = () => useContext(SiteContentContext);

export const applyTheme = (c) => {
  const root = document.documentElement;
  root.style.setProperty("--ekal-accent", c.color_accent || DEFAULT_CONTENT.color_accent);
  root.style.setProperty("--ekal-amber", c.color_amber || DEFAULT_CONTENT.color_amber);
  root.style.setProperty("--ekal-bg", c.color_bg || DEFAULT_CONTENT.color_bg);
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  useEffect(() => {
    api
      .get("/site-content")
      .then((r) => {
        if (r.data && typeof r.data === "object" && Object.keys(r.data).length) {
          setContent({ ...DEFAULT_CONTENT, ...r.data });
        }
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    applyTheme(content);
  }, [content]);
  return <SiteContentContext.Provider value={content}>{children}</SiteContentContext.Provider>;
};
